// ExtrudePreview — показывает полупрозрачный preview-тела при активной
// операции «Выдавить» + drag-стрелку для изменения distance.
//
// Live-preview строится не через WASM (медленно), а через простую
// геометрию three.js: параллелепипед из вершин полигона + высота, или
// цилиндр для окружности.

import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

import useCadStore from '../store/useCadStore';
import { findProfiles, profilePolygonVertices, profileCenter, profileArea, pointInProfile } from './sketchProfiles';


// Quaternion для поворота group, СОВПАДАЮЩЕГО с replicad sketchOnPlane.
// Для CUSTOM-плоскости используем явные xDir/normal из faceProfile.
function planeQuaternion(planeId, customPlane) {
    let xDir, yDir, zDir;
    if (customPlane && planeId === 'CUSTOM') {
        xDir = new THREE.Vector3(...customPlane.xDir);
        zDir = new THREE.Vector3(...customPlane.normal);
        // yDir = zDir × xDir (правая система координат)
        yDir = new THREE.Vector3().crossVectors(zDir, xDir).normalize();
    } else if (planeId === 'XZ') {
        xDir = new THREE.Vector3(1, 0, 0);
        yDir = new THREE.Vector3(0, 0, 1);
        zDir = new THREE.Vector3(0, -1, 0);
    } else if (planeId === 'YZ') {
        xDir = new THREE.Vector3(0, 1, 0);
        yDir = new THREE.Vector3(0, 0, 1);
        zDir = new THREE.Vector3(1, 0, 0);
    } else {
        xDir = new THREE.Vector3(1, 0, 0);
        yDir = new THREE.Vector3(0, 1, 0);
        zDir = new THREE.Vector3(0, 0, 1);
    }
    const m = new THREE.Matrix4().makeBasis(xDir, yDir, zDir);
    return new THREE.Quaternion().setFromRotationMatrix(m);
}


// Геометрия preview-тела из профиля + distance + direction.
//
// distance может быть отрицательным (extrude в -Z).
// Тело занимает по Z: [zMin, zMax]:
//   one:        [0, distance] (или [distance, 0] если distance<0)
//   symmetric:  [-|d|/2, +|d|/2]
//   two:        [-|d|, +|d|]
function buildPreviewGeometry(profile, sketch, distance, direction) {
    let zMin, zMax;
    if (direction === 'symmetric') {
        const half = Math.abs(distance) / 2;
        zMin = -half; zMax = half;
    } else if (direction === 'two') {
        const mag = Math.abs(distance);
        zMin = -mag; zMax = mag;
    } else {
        // 'one'
        if (distance >= 0) { zMin = 0; zMax = distance; }
        else                { zMin = distance; zMax = 0; }
    }
    const depth = zMax - zMin;
    if (depth < 0.01) return null;

    // holeVerts — массив контуров-дырок (каждый = [{x,y}...]); для circle-дырок
    // это тесселированная окружность. Общий для circle и polygon внешнего контура.
    const holeVerts = Array.isArray(profile.holeVerts) ? profile.holeVerts : [];

    if (profile.kind === 'circle' && holeVerts.length === 0) {
        const g = new THREE.CylinderGeometry(profile.r, profile.r, depth, 48);
        // CylinderGeometry — ось Y, поворачиваем по Z
        g.rotateX(Math.PI / 2);
        // Центр цилиндра ставим на середину Z-интервала
        g.translate(profile.cx, profile.cy, (zMin + zMax) / 2);
        return g;
    }
    // Внешний контур как THREE.Shape (окружность или полигон) + дырки.
    let outerVerts;
    if (profile.kind === 'circle') {
        outerVerts = [];
        const N = 48;
        for (let i = 0; i < N; i++) {
            const a = (i / N) * Math.PI * 2;
            outerVerts.push({ x: profile.cx + Math.cos(a) * profile.r,
                              y: profile.cy + Math.sin(a) * profile.r });
        }
    } else if (profile.kind === 'polygon' || profile.kind === 'region') {
        /*
         * 'region' — профиль из планарного разбиения эскиза (sketchArrangement,
         * 26.07). Он приходит с ГОТОВЫМ контуром в verts.
         *
         * Раньше эта ветка ловила только 'polygon', а всё остальное падало в
         * `return null` ниже — поэтому после перехода на разбиение живое превью
         * выдавливания пропало: тянешь стрелку, число меняется, а тело не
         * растёт. Профиль при этом подсвечивался (SketchOverlay про region
         * знает), отчего казалось, что «сломалось на ровном месте».
         */
        outerVerts = profile.verts && profile.verts.length > 0
            ? profile.verts
            : profilePolygonVertices(profile, sketch);
    } else {
        return null;
    }
    if (outerVerts.length < 3) return null;
    const shape = new THREE.Shape();
    shape.moveTo(outerVerts[0].x, outerVerts[0].y);
    for (let i = 1; i < outerVerts.length; i++) shape.lineTo(outerVerts[i].x, outerVerts[i].y);
    shape.closePath();
    for (const hv of holeVerts) {
        if (!hv || hv.length < 3) continue;
        const path = new THREE.Path();
        path.moveTo(hv[0].x, hv[0].y);
        for (let i = 1; i < hv.length; i++) path.lineTo(hv[i].x, hv[i].y);
        path.closePath();
        shape.holes.push(path);
    }
    const opts = { depth, bevelEnabled: false, curveSegments: 12 };
    const g = new THREE.ExtrudeGeometry(shape, opts);
    g.translate(0, 0, zMin);
    return g;
}


// Drag-стрелка перпендикулярна плоскости эскиза (локальная +Z).
// Полноценная 3D: тонкий цилиндр-стержень + конус-наконечник, как в Fusion 360.
// Конус ориентирован точно вдоль направления выдавливания (не billboard) —
// при взгляде сбоку видно его силуэт, при взгляде с торца — кружок.
const ExtrudeArrow = ({ origin, distance, onChange }) => {
    const { camera, gl, controls } = useThree();
    const groupRef = useRef();
    const draggingRef = useRef(false);
    const startRef = useRef({});
    const orbitsWasEnabledRef = useRef(true);

    // Знак направления выдавливания
    const sign = distance < 0 ? -1 : 1;
    // Длина стрелки = текущая дистанция, но не меньше 10 (чтобы стрелка
    // была видна и захватываема при distance близком к нулю)
    const arrowLen = Math.max(Math.abs(distance), 10);
    // Размеры элементов стрелки (в мм мира)
    const STEM_RADIUS = 0.6;
    const HEAD_RADIUS = 2.4;
    const HEAD_LENGTH = 5.5;
    // Стержень тянется от 0 до основания конуса
    const stemLen = Math.max(arrowLen - HEAD_LENGTH, 0.1);
    // Z-координаты в локальной системе
    const stemMidZ = sign * (stemLen / 2);
    const headBaseZ = sign * stemLen;
    const headTipZ  = sign * arrowLen;

    // Drag handler
    useEffect(() => {
        const dom = gl.domElement;
        const onPointerMove = (e) => {
            if (!draggingRef.current) return;
            const ax = startRef.current.axisScreen;
            if (!ax) return;
            const dx = e.clientX - startRef.current.clientX;
            const dy = e.clientY - startRef.current.clientY;
            const proj = dx * ax.x + dy * ax.y;
            const mmPerPx = startRef.current.mmPerPx;
            // Поддерживаем отрицательный distance — drag вниз через
            // плоскость даёт extrude в -Z направлении.
            const newDist = startRef.current.distance + proj * mmPerPx;
            // Защита от 0 (ничего не выдавливается)
            const safe = Math.abs(newDist) < 0.5
                ? (newDist < 0 ? -0.5 : 0.5)
                : newDist;
            onChange(Math.round(safe));
        };
        const onPointerUp = () => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            // Возвращаем OrbitControls
            if (controls && 'enabled' in controls) {
                controls.enabled = orbitsWasEnabledRef.current;
            }
            try { dom.releasePointerCapture(startRef.current.pointerId); } catch (_) {}
        };
        dom.addEventListener('pointermove', onPointerMove);
        dom.addEventListener('pointerup', onPointerUp);
        dom.addEventListener('pointercancel', onPointerUp);
        return () => {
            dom.removeEventListener('pointermove', onPointerMove);
            dom.removeEventListener('pointerup', onPointerUp);
            dom.removeEventListener('pointercancel', onPointerUp);
        };
    }, [gl, onChange, controls]);

    const onArrowDown = useCallback((e) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        if (!groupRef.current) return;
        const dom = gl.domElement;
        const rect = dom.getBoundingClientRect();
        const project = (v) => {
            const w = new THREE.Vector3(v[0], v[1], v[2]);
            groupRef.current.updateMatrixWorld(true);
            w.applyMatrix4(groupRef.current.matrixWorld);
            w.project(camera);
            return {
                x: rect.left + (w.x + 1) / 2 * rect.width,
                y: rect.top + (1 - w.y) / 2 * rect.height,
            };
        };
        const O = project([0, 0, 0]);
        const Z = project([0, 0, 1]);
        const sx = Z.x - O.x, sy = Z.y - O.y;
        const slen = Math.sqrt(sx*sx + sy*sy) || 1;
        const axisScreen = { x: sx / slen, y: sy / slen };
        const mmPerPx = 1 / slen;

        // Блокируем OrbitControls
        if (controls && 'enabled' in controls) {
            orbitsWasEnabledRef.current = controls.enabled;
            controls.enabled = false;
        }

        draggingRef.current = true;
        startRef.current = {
            clientX: e.clientX,
            clientY: e.clientY,
            distance: distance,
            axisScreen,
            mmPerPx,
            pointerId: e.pointerId,
        };
        try { dom.setPointerCapture(e.pointerId); } catch (_) {}
    }, [camera, gl, distance, controls]);

    // Геометрия цилиндра-стержня и конуса. CylinderGeometry/ConeGeometry
    // в Three.js по умолчанию ориентированы вдоль оси Y → вращаем по X на
    // 90° чтобы стояли вдоль оси Z (направление выдавливания).
    const stemGeom = useMemo(() => {
        const g = new THREE.CylinderGeometry(STEM_RADIUS, STEM_RADIUS, stemLen, 16);
        g.rotateX(Math.PI / 2);
        return g;
    }, [stemLen]);
    const headGeom = useMemo(() => {
        const g = new THREE.ConeGeometry(HEAD_RADIUS, HEAD_LENGTH, 24);
        // Конус «острие в +Y» → +Z. Если sign отрицательный, дополнительно
        // поворачиваем чтобы остриё смотрело в -Z.
        g.rotateX(Math.PI / 2);
        if (sign < 0) g.rotateY(Math.PI);
        return g;
    }, [sign]);

    return (
        <group ref={groupRef} position={origin}>
            {/* Цилиндр-стержень (прозрачно-синий) */}
            <mesh geometry={stemGeom} position={[0, 0, stemMidZ]} onPointerDown={onArrowDown}>
                <meshBasicMaterial color="#3a78ff" depthTest={false}/>
            </mesh>
            {/* Конус-наконечник — основание на конце стержня, остриё в направлении выдавливания */}
            <mesh
                geometry={headGeom}
                position={[0, 0, (headBaseZ + headTipZ) / 2]}
                onPointerDown={onArrowDown}
            >
                <meshBasicMaterial color="#3a78ff" depthTest={false}/>
            </mesh>
        </group>
    );
};


const ExtrudePreviewBody = ({ profile, sketch, distance, direction, operation }) => {
    const geometry = useMemo(
        () => buildPreviewGeometry(profile, sketch, distance, direction),
        [profile, sketch, distance, direction]
    );
    if (!geometry) return null;
    // Цвет превью как в Fusion 360:
    //   вырезание/пересечение (убираем материал) → КРАСНЫЙ,
    //   добавление/новое тело/объединение        → синий.
    const isCut = operation === 'cut' || operation === 'intersect';
    const color = isCut ? '#e2483d' : '#3a78ff';
    return (
        <mesh geometry={geometry}>
            <meshStandardMaterial
                color={color}
                emissive={color}
                emissiveIntensity={0.25}
                transparent
                opacity={isCut ? 0.4 : 0.35}
                metalness={0.05}
                roughness={0.6}
            />
        </mesh>
    );
};


// Утилита: world-позиция точки на плоскости replicad для данного planeName/offset.
// Для CUSTOM-плоскости возвращает её origin напрямую.
function planeWorldPos(planeName, offset, customPlane) {
    if (customPlane && planeName === 'CUSTOM') return [...customPlane.origin];
    if (!offset) return [0, 0, 0];
    if (planeName === 'XZ') return [0, -offset, 0];
    if (planeName === 'YZ') return [offset, 0, 0];
    return [0, 0, offset];
}


// Центр полигона (среднее вершин) — для размещения стрелки.
function polygonCenter(verts) {
    if (!verts || verts.length === 0) return { x: 0, y: 0 };
    let cx = 0, cy = 0;
    for (const v of verts) { cx += v.x; cy += v.y; }
    return { x: cx / verts.length, y: cy / verts.length };
}


const ExtrudePreview = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const sketches = useCadStore((s) => s.doc.sketches);

    // === Все хуки ВСЕГДА вызываются — порядок должен быть стабильным ===

    // Определяем источник: либо профиль эскиза, либо грань тела.
    const isFaceMode = !!pendingFeature?.faceProfile;
    const ref = pendingFeature?.profileRefs?.[0] || null;
    const [sketchId, profileId] = ref ? ref.split('/') : [null, null];
    const sketch = sketches.find((s) => s.id === sketchId) || null;

    // Параметры плоскости — из faceProfile или из sketch.
    const planeId = isFaceMode
        ? pendingFeature.faceProfile.planeName
        : (sketch?.planeRef?.plane || 'XY');
    const planeOffset = isFaceMode
        ? pendingFeature.faceProfile.planeOffset
        : (sketch?.planeRef?.offset || 0);
    // Для CUSTOM-плоскости собираем basis: из faceProfile (face-extrude)
    // или напрямую из sketch.planeRef (sketch-extrude на наклонной грани).
    const customPlane = (() => {
        if (isFaceMode) return pendingFeature.faceProfile.customPlane;
        if (sketch?.planeRef?.plane === 'CUSTOM'
            && sketch.planeRef.origin && sketch.planeRef.xDir && sketch.planeRef.normal) {
            return {
                origin: sketch.planeRef.origin,
                xDir:   sketch.planeRef.xDir,
                normal: sketch.planeRef.normal,
            };
        }
        return null;
    })();

    const quat = useMemo(
        () => planeQuaternion(planeId, customPlane),
        [planeId, customPlane]
    );
    const planePos = useMemo(
        () => planeWorldPos(planeId, planeOffset, customPlane),
        [planeId, planeOffset, customPlane]
    );

    // Профили эскиза (только для sketch-режима)
    const profiles = useMemo(
        () => (sketch ? findProfiles(sketch) : []),
        [sketch]
    );
    const sketchProfile = useMemo(
        () => profiles.find((p) => p.id === profileId) || null,
        [profiles, profileId]
    );

    // Профиль для preview-тела (sketch- или face-вариант) + дырки.
    const previewProfile = useMemo(() => {
        if (isFaceMode) {
            return { kind: 'polygon', verts: pendingFeature.faceProfile.verts };
        }
        if (!sketchProfile || !sketch) return sketchProfile;
        // Находим дырки (контуры внутри выбранного профиля) и тесселируем их
        // вершины — чтобы preview показывал отверстие, как и итоговое тело.
        const outerArea = profileArea(sketchProfile, sketch);
        const holeVerts = [];
        for (const other of profiles) {
            if (other.id === sketchProfile.id) continue;
            if (!(profileArea(other, sketch) < outerArea)) continue;
            const c = profileCenter(other, sketch);
            if (!pointInProfile(sketchProfile, c.x, c.y, sketch)) continue;
            if (other.kind === 'circle') {
                const N = 48, hv = [];
                for (let i = 0; i < N; i++) {
                    const a = (i / N) * Math.PI * 2;
                    hv.push({ x: other.cx + Math.cos(a) * other.r,
                              y: other.cy + Math.sin(a) * other.r });
                }
                holeVerts.push(hv);
            } else {
                const hv = profilePolygonVertices(other, sketch);
                if (hv.length >= 3) holeVerts.push(hv);
            }
        }
        return holeVerts.length ? { ...sketchProfile, holeVerts } : sketchProfile;
    }, [isFaceMode, pendingFeature, sketchProfile, sketch, profiles]);

    // Центр профиля — точка origin для drag-стрелки.
    const center = useMemo(() => {
        if (isFaceMode) return polygonCenter(pendingFeature.faceProfile.verts);
        if (sketchProfile && sketch) return profileCenter(sketchProfile, sketch);
        return { x: 0, y: 0 };
    }, [isFaceMode, pendingFeature, sketchProfile, sketch]);

    // sketch для face-extrude передаём как { primitives: [] } — buildPreviewGeometry
    // для polygon использует profile.verts напрямую (через profilePolygonVertices,
    // но в face-варианте verts уже у нас, поэтому строим preview-геометрию иначе).
    const fakeSketch = useMemo(() => ({ primitives: [] }), []);

    // Профиль на грани существующего тела? (face-extrude или эскиз на грани).
    const profileOnBody = isFaceMode || (sketch?.planeRef?.kind === 'face');

    // Инициализация авто-операции при старте: если профиль на грани тела и
    // операция ещё дефолтная 'new' (не выбрана вручную) — ставим по текущему
    // направлению (наружу=add / внутрь=cut). Дальше onArrowChange поддерживает.
    useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'extrude') return;
        if (!profileOnBody || pendingFeature.operationManual) return;
        if (pendingFeature.operation !== 'new') return;
        const s = isFaceMode ? (pendingFeature?.faceProfile?.sign || 1) : 1;
        const eff = (pendingFeature.distance || 0) * s;
        if (eff !== 0) {
            updatePendingFeature({ operation: eff < 0 ? 'cut' : 'add' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingFeature?.kind, profileOnBody]);

    const onArrowChange = (newDist) => {
        const patch = { distance: newDist };
        applyAutoOperation(patch, newDist);
        updatePendingFeature(patch);
    };

    // Авто-операция по направлению (как в Fusion), когда профиль лежит НА ГРАНИ
    // существующего тела: ВНУТРЬ тела = ВЫРЕЗАТЬ, наружу = ДОБАВИТЬ.
    //   - face-extrude: sign грани кодирует связь нормали с zDir плоскости.
    //   - sketch-extrude на грани тела (planeRef.kind==='face'): zDir плоскости
    //     эскиза = нормаль грани наружу тела, поэтому effectiveDist = distance
    //     (sign=1). >0 = наружу (add), <0 = внутрь (cut).
    // Не трогаем, если юзер выбрал операцию вручную (operationManual).
    const applyAutoOperation = (patch, rawDist) => {
        if (pendingFeature?.operationManual || rawDist === 0) return;
        const onFace = isFaceMode || (sketch?.planeRef?.kind === 'face');
        if (!onFace) return;
        const s = isFaceMode ? (pendingFeature?.faceProfile?.sign || 1) : 1;
        const eff = rawDist * s;
        patch.operation = eff < 0 ? 'cut' : 'add';
    };

    // === Условные return ПОСЛЕ всех хуков ===
    if (!pendingFeature || pendingFeature.kind !== 'extrude') return null;

    // Знак выдавливания: sign из faceProfile компенсирует zDir плоскости
    // replicad (XZ/YZ). Направление (наружу/внутрь) задаёт юзер через знак
    // distance — мы НЕ инвертируем автоматически по operation, иначе preview
    // перепрыгивает при смене operation в форме.
    const sign = isFaceMode ? (pendingFeature.faceProfile.sign || 1) : 1;
    const effectiveDist = pendingFeature.distance * sign;

    if (isFaceMode) {
        if (!previewProfile) return null;
        return (
            <group position={planePos} quaternion={quat}>
                <ExtrudePreviewBody
                    profile={previewProfile}
                    sketch={fakeSketch}
                    distance={effectiveDist}
                    direction={pendingFeature.direction}
                    operation={pendingFeature.operation}
                />
                <ExtrudeArrow
                    origin={[center.x, center.y, 0]}
                    distance={effectiveDist}
                    onChange={(newDist) => onArrowChange(newDist * sign)}
                />
            </group>
        );
    }

    // Sketch-вариант
    if (!ref || !sketch || !sketchProfile) return null;
    // distance несёт знак напрямую — preview/стрелка следуют его направлению.
    // НЕ применяем automatic-инверсию по operation: смена operation
    // (add/cut/new) не должна разворачивать preview — юзер сам управляет
    // направлением через drag-стрелку или ввод значения.
    // Все выбранные профили этого эскиза — для превью каждого при
    // множественном выборе (Ctrl). Первый несёт стрелку.
    const allRefs = pendingFeature.profileRefs || [];
    const extraProfiles = allRefs
        .map((r) => r.split('/')[1])
        .filter((pid) => pid && pid !== profileId)
        .map((pid) => profiles.find((p) => p.id === pid))
        .filter(Boolean);

    return (
        <group position={planePos} quaternion={quat}>
            <ExtrudePreviewBody
                profile={sketchProfile}
                sketch={sketch}
                distance={pendingFeature.distance}
                direction={pendingFeature.direction}
                operation={pendingFeature.operation}
            />
            {extraProfiles.map((p) => (
                <ExtrudePreviewBody
                    key={p.id}
                    profile={p}
                    sketch={sketch}
                    distance={pendingFeature.distance}
                    direction={pendingFeature.direction}
                    operation={pendingFeature.operation}
                />
            ))}
            <ExtrudeArrow
                origin={[center.x, center.y, 0]}
                distance={pendingFeature.distance}
                onChange={onArrowChange}
            />
        </group>
    );
};

export default ExtrudePreview;
